import AWSAppSyncClient from "aws-appsync";
import axios from "axios";
import is from "is";
import { nanoid } from "nanoid";
import format from "date-fns/format";
import numeral from "../lib/numeral";
import {
  jsonCopy,
  compressJson,
  decompressJson,
  isValidDate,
  isInThePast,
  isSameText,
  isJsonString,
  getAsJson,
  isEmailAddress,
  getGroupByData,
  getObjectSize,
  getDummyImage,
  tryParseInt,
  getFileIdFromDocumentId,
  getDayDifferenceToEndDate,
  formatDateTimeForFileName,
  base64UrlToJson,
} from "../utils/helper";

import gql from "graphql-tag";
const appSyncConfig = {
  aws_appsync_graphqlEndpoint: process.env.REACT_APP_APPSYNC_ENDPOINT,
  aws_appsync_region: "eu-central-1",
  aws_appsync_authenticationType: "API_KEY",
  aws_appsync_apiKey: process.env.REACT_APP_APPSYNC_APIKEY,
};
const maxElementCountForFree = 25;

export class StoreAppSync {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.updateInvitationStatusSubscription = undefined;
    this.newInvitationSubscription = undefined;
    this.newSubmitSubscription = undefined;
    this.invitationProtocol = undefined;

    this.processNotificationsNewSubmit =
      this.processNotificationsNewSubmit.bind(this);
    this.processNotificationsUpdateInvitationStatus =
      this.processNotificationsUpdateInvitationStatus.bind(this);
    this.processNotificationsNewNotification =
      this.processNotificationsNewNotification.bind(this);

    if (!this.client) {
      this.createClient();
    }
  }

  componentWillUnmount() {
    console.log("StoreAppSync Will UNMOUNT!");
    try {
      if (this.newSubmitSubscription) this.newSubmitSubscription.unsubscribe();
      if (this.updateInvitationStatusSubscription)
        this.updateInvitationStatusSubscription.unsubscribe();
    } catch (err) {
      console.log("ERROR releasing StoreAppSync");
    }
  }

  createClient() {
    this.client = new AWSAppSyncClient({
      url: appSyncConfig.aws_appsync_graphqlEndpoint,
      region: appSyncConfig.aws_appsync_region,
      auth: {
        type: appSyncConfig.aws_appsync_authenticationType,
        apiKey: appSyncConfig.aws_appsync_apiKey,
      },
      disableOffline: true,
    });
  }

  // =================================================================
  //
  // SUBSCRIPTIONS
  //
  // =================================================================

  registerUserSubscriptions() {
    // new notification
    const subqueryNewNotification = gql(`
    subscription NewNotification {
      onCreateArNotification(emailReceiver: "${this.rootStore.main.userEmail}") {
        id
        type
        emailReceiver
        header
        content
        created
      }
    }`);
    this.newNotificationSubscription = this.client.subscribe({
      query: subqueryNewNotification,
    });

    this.newNotificationSubscription.subscribe({
      next: this.processNotificationsNewNotification, //realtimeResults,
      complete: console.log,
      error: console.log,
    });

    // new submit
    const subqueryNewSubmit = gql(`
    subscription NewSubmit {
      onCreateArSubmit(emailNotification: "${this.rootStore.main.userEmail}") {
        id
        documentId
        configurationId
        invitationId
        emailSender
        title
        status
        comment
        summaryData
        created
      }
    }`);
    this.newSubmitSubscription = this.client.subscribe({
      query: subqueryNewSubmit,
    });

    this.newSubmitSubscription.subscribe({
      next: this.processNotificationsNewSubmit, //realtimeResults,
      complete: console.log,
      error: console.log,
    });

    // updated invitation
    const subqueryUpdateInvitationStatus = gql(`
    subscription UpdateInvitationStatus {
      onUpdateArInvitationStatus(emailSender: "${this.rootStore.main.userEmail}") {
        id
        name
        status
        emailSender
        isDeleted
        isArchived
        modified
      }
    }`);
    this.updateInvitationStatusSubscription = this.client.subscribe({
      query: subqueryUpdateInvitationStatus,
    });

    this.updateInvitationStatusSubscription.subscribe({
      next: this.processNotificationsUpdateInvitationStatus, //realtimeResults,
      complete: console.log,
      error: console.log,
    });
  }

  unregisterUserSubscriptions() {
    // Unsubscribe
    try {
      this.updateInvitationStatusSubscription.unsubscribe();
      this.newNotificationSubscription.subscribe();
    } catch (err) {
      console.log("ERROR unregisterUserSubscriptions", err);
    }
  }

  processNotificationsUpdateInvitationStatus(data) {
    if (!data) return;

    try {
      const statusUpdate = data.data.onUpdateArInvitationStatus;
      if (statusUpdate?.id && statusUpdate?.status) {
        setTimeout(() => {
          this.rootStore?.sharing?.updatedInvitationStatusFromNotification(
            statusUpdate
          );
        }, 1000);
      }
    } catch (err) {
      console.log("ERROR processNotificationsUpdateInvitationStatus", err);
    }
  }

  // =================================================================
  //
  // MISC
  //
  // =================================================================

  setDefaultSettings() {
    // const defaultImageUrl =
    //   "https://images.unsplash.com/photo-1542241800-918eb3c793a0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxODA1OTB8MHwxfHNlYXJjaHwxOXx8c25vd2JvYXJkJTIwd2hpdGUlMjBub3ZlbWJlcnxlbnwwfHx8fDE2NDMyOTE3NDM&ixlib=rb-1.2.1&q=80&w=1200";
    // this.rootStore.main.setViewBackgroundImage("home", defaultImageUrl);
  }

  // =================================================================
  //
  // USER
  //
  // =================================================================

  async getUserSubscription(subscriptionId) {
    const queryGetUserSubscription = gql(`
      query getSubscription {
        getArSubscription(id: "${subscriptionId}") {
          id
          stripeId
          customerId
          subscriptionStart
          subscriptionEnd
          subscriptionPlan
          subscriptionInterval
          subscriptionType
          limitAppElements
        }
    }`);

    let subscription;
    try {
      let awsResult = await this.client.query({
        query: queryGetUserSubscription,
      });
      if (!awsResult) return;
      subscription = awsResult.data?.getArSubscription;
    } catch (error) {
      console.log("ERROR getUserSubscription", error);
    }
    return subscription;
  }

  async getUserInfo(user) {
    console.log("INFO userInfo", user);
    if (!user || !user.email) return;

    this.rootStore.main.setUserDataLoading(true);

    // look for a valid email
    let tempUserEmail = user.email;

    // try to get an email from an unvalid format
    if (!isEmailAddress(tempUserEmail)) {
      if (tempUserEmail?.includes(",")) {
        const tempList = tempUserEmail.replaceAll(" ", "")?.split(",");
        if (tempList?.length > 1) {
          tempUserEmail = tempList[0];
          if (!isEmailAddress(tempUserEmail) && tempList.length > 1) {
            tempUserEmail = tempList[1];
          }
        }
      }
      if (!isEmailAddress(tempUserEmail)) {
        console.log("INFO tempUserEmail is not valid");
        this.rootStore.main.trackEvent("ERROR UserEmail is not valid", user);
        return;
      }
    }

    const userId = user.id;
    const publicId = this.rootStore.main.getLocalStoreValue("publicUserId");
    const userEmail = tempUserEmail.toLowerCase();
    const authType = this.rootStore.main.isMicrosoftLogin ? "m365" : "hanko";
    const userData = { authType: authType };
    const data = JSON.stringify(userData);
    const queryGetUser = gql(`
      query getUser {
        getArUser(id: "${userId}") {
          id
          publicId
          email
          data
          subscriptionId
          subscriptionPlan
          licenseInfo
          preferredLanguage
          created
        }
    }`);
    const mutationAddUser = gql(`
      mutation addUser($data: AWSJSON) {
        createArUser(id: "${userId}", publicId: "${publicId}", email: "${userEmail}", data: $data) {
          id
          publicId
          email
          data
          subscriptionId
          subscriptionPlan
          licenseInfo
          preferredLanguage
          created
        }
    }`);

    let awsResult;

    try {
      awsResult = await this.client.query({ query: queryGetUser });
      if (awsResult) {
        // log the size of userInfo
        try {
          const roughObjSize = JSON.stringify(awsResult).length;
          console.log("UserINFO size", roughObjSize);
        } catch (err) {
          console.log("ERROR objSize length", err);
        }
      } else {
        console.log("INFO no getUser", user);
        this.rootStore.main.trackEvent(
          "INFO no getUserInfo queryGetUser",
          user
        );
        // return;
      }
    } catch (err) {
      console.log("ERROR getUserInfo queryGetUser", err, user);
      this.rootStore.main.trackEvent(
        "ERROR getUserInfo queryGetUser",
        user,
        err
      );
    }

    try {
      let user = awsResult?.data?.getArUser;
      if (!user) {
        console.log("ERROR no existing user - create");
        // it is a new user
        this.setDefaultSettings();
        // create a new entry if there was no result
        const awsResultAdd = await this.client.mutate({
          mutation: mutationAddUser,
          variables: { data: data },
        });
        user = awsResultAdd.data.createArUser;
      }
      const created = new Date(user?.created ? user.created : new Date());
      const daysActive = getDayDifferenceToEndDate(created);
      let preferredLanguage =
        user?.preferredLanguage ?? this.rootStore.main.userLanguage;
      if (!preferredLanguage) {
        preferredLanguage = "en-US";
      }
      // read the subscription
      let subscriptionInfo;
      const subscriptionId = user?.subscriptionId;
      const subscriptionPlan = user?.subscriptionPlan;

      if (subscriptionId) {
        subscriptionInfo = await this.getUserSubscription(subscriptionId);
      }
      // ===================================
      // read the manual user license
      // ===================================
      let licenseInfo = getAsJson(user?.licenseInfo) ?? {};
      if (
        ["pro-50", "pro-100", "pro-250", "pro-500"].includes(
          licenseInfo.license
        )
      ) {
        licenseInfo.license = "pro";
      }
      let licenseType = licenseInfo.license
        ? licenseInfo.license
        : subscriptionPlan
          ? subscriptionPlan
          : "free";
      let validUntil = licenseInfo.validUntil;
      const withLiveUpdate = true;

      // ===================================
      // read the subscription info
      // ===================================
      if (subscriptionInfo) {
        if (subscriptionInfo.subscriptionPlan) {
          licenseType = subscriptionInfo.subscriptionPlan;
        }
        if (subscriptionInfo.subscriptionEnd) {
          validUntil = new Date(subscriptionInfo.subscriptionEnd);
        }
      }

      if (
        (!licenseType || licenseType === "free") &&
        subscriptionPlan &&
        subscriptionPlan !== "free"
      )
        licenseType = subscriptionPlan;

      // ===================================
      // manual subscriptionPlan overrides
      // ===================================

      if (subscriptionPlan) {
        licenseType = subscriptionPlan;
      }

      // get the default functionality
      const planInfo = this.rootStore.main.getLicenseInformation(licenseType);

      // const trialUntil = new Date(2023, 2, 31);
      // licenseInfo.trialUntil
      //   ? licenseInfo.trialUntil
      //   : new Date(2023, 3, 31);
      const trialDaysLeft = -1; //getDayDifferenceToEndDate(new Date(trialUntil));
      let limitAppElements = licenseInfo.limitAppElements
        ? licenseInfo.limitAppElements
        : (planInfo?.limitAppElements ?? maxElementCountForFree);
      if (limitAppElements < maxElementCountForFree) {
        limitAppElements = maxElementCountForFree;
      }
      // if (trialDaysLeft > 0) {
      //   limitAppElements = 1000;
      //   limitShares = 50;
      // }
      // set the user license data
      this.rootStore.main.setUserProperties(
        limitAppElements,
        licenseType,
        validUntil,
        trialDaysLeft,
        withLiveUpdate,
        preferredLanguage,
        subscriptionInfo,
        authType,
        daysActive
      );
      // load the rest of the data
      // this.getUserLinkedInfo();
      this.getUserDomainSettings();

      this.rootStore.main.setUserDataLoaded(true);
      this.rootStore.main.setUserDataLoading(false);

      // register the subscriptions
      this.registerUserSubscriptions();
    } catch (err) {
      console.log("ERROR getUserInfo", err);
    }
    return true;
  }

  async logPublicUser(userId, dataInfo = {}) {
    if (!userId) return;
    let jsonString = JSON.stringify(dataInfo);
    let cookies = dataInfo.cookies ? dataInfo.cookies : {};
    let cookiesString = JSON.stringify(cookies);

    const mutationCreatePublicUser = gql(`
      mutation CreateArPublicUser($data: String, $cookies: AWSJSON) {
        createArPublicUser(id: "${userId}", data: $data, cookies: $cookies) {
          id
          created
        }
    }`);

    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationCreatePublicUser,
        variables: { data: jsonString, cookies: cookiesString },
      });
    } catch (err) {
      console.log("ERROR createPublicUser", err);
    }
    return true;
  }

  async getUserLinkedInfo() {
    console.count("getUserLinkedInfo");
    if (!this.rootStore.main.userId) return;

    const userId = this.rootStore.main.userId;
    const queryGetUserLinkedInfo = gql(`
      query getUser {
        getArUser(id: "${userId}") {
          id
          email
          invitationsInbound {
            items {
              id
              documentId
              name
              emailSender
              status
              type
              data
              designInfo
              created
              modified
            }
          }
          invitationsOutbound {
            items {
              id
              name
              emailReceiver
              status
              type
              data
              cacheData
              designInfo
              created
              modified
            }
          }
        }
    }`);

    try {
      let awsResult = await this.client.query({
        query: queryGetUserLinkedInfo,
        fetchPolicy: "network-only",
      });
      if (!awsResult) return;
      // log the size of userInfo
      try {
        const roughObjSize = JSON.stringify(awsResult).length;
        console.log("UserLinkedINFO size", roughObjSize);
      } catch (err) {
        console.log("ERROR objSize length", err);
      }
      let user = awsResult.data?.getArUser ?? {};
      // =====================
      // invitations inbound
      // =====================
      try {
        user.invitationsInbound?.items.forEach((invitation) => {
          if (invitation.data) invitation.data = getAsJson(invitation.data);
          if (invitation.designInfo)
            invitation.designInfo = getAsJson(invitation.designInfo);
          if (invitation.hasOwnProperty("__typename"))
            delete invitation["__typename"];
        });
        const userInvitationsInbound = user.invitationsInbound?.items;
        this.rootStore.sharing.setInvitationsInbound(
          jsonCopy(userInvitationsInbound) ?? []
        );
      } catch (err) {
        console.log("ERROR getUserInfo invitationsInbound", err);
      }
      // =====================
      // invitation outbound
      // =====================
      try {
        user.invitationsOutbound?.items.forEach((invitation) => {
          if (invitation.data) {
            const jsonData = getAsJson(invitation.data);
            if (jsonData.hasOwnProperty("limitElementCount"))
              delete jsonData["limitElementCount"];
            if (jsonData.hasOwnProperty("licenseCheck"))
              delete jsonData["licenseCheck"];
            if (jsonData.hasOwnProperty("licenseType"))
              delete jsonData["licenseType"];
            invitation.data = jsonData;
          }

          if (invitation.designInfo)
            invitation.designInfo = getAsJson(invitation.designInfo);
          if (invitation.hasOwnProperty("__typename"))
            delete invitation["__typename"];
        });
        const userInvitationsOutbound = user.invitationsOutbound?.items;
        this.rootStore.sharing.setInvitationsOutbound(
          jsonCopy(userInvitationsOutbound)
        );
      } catch (err) {
        console.log("ERROR getUserInfo invitationsOutbound", err);
      }

      return awsResult;
    } catch (err) {
      console.log("ERROR getUserLinkedInfo", err, userId);
    }
  }

  async updateUserPreferredLanguage(language) {
    const userId = this.rootStore.main.userMe?.id;
    if (!userId) return;

    const mutationUpdateUserPreferredLanguage = gql(`
      mutation UpdateArUserPreferredLanguage {
        updateArUserPreferredLanguage(id: "${userId}", preferredLanguage: "${language}") {
          id
          preferredLanguage
        }
    }`);

    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationUpdateUserPreferredLanguage,
      });
    } catch (err) {
      console.log("ERROR updateUserPreferredLanguage", err);
    }
  }

  async getUserConnectors() {
    let list = [];
    const userEmail = this.rootStore.main.userEmail;
    const userTenantId = this.rootStore.main.userTenantId;

    // check if it is not the public tenant
    const useTenantId =
      userTenantId && userTenantId !== "9188040d-6c67-4c5b-b112-36a304b66dad";

    // ===================================================
    // get connector by tenantId
    // ===================================================

    if (useTenantId) {
      const queryArConnectorsByTenantId = gql(`
        query GetConnectorsByTenant {
          queryArConnectorsByTenantId(tenantId: "${userTenantId}", limit: 1000) {
            items {
              id
              tenantId
              isDeleted
              isTenantConnector
              tokenRefreshValidUntil
              connectorType
              title
              emailCreator
              created
              modified
            }
            nextToken
          }
        }`);

      try {
        let awsResult = await this.client.query({
          query: queryArConnectorsByTenantId,
          fetchPolicy: "network-only",
        });
        const awsConnectors = awsResult?.data?.queryArConnectorsByTenantId;
        awsConnectors?.items.forEach((item) => {
          let isOK = false;
          if (isSameText(userEmail, item.emailCreator)) isOK = true;
          if (item.connectorType === "app-consent") isOK = false;
          if (item.isTenantConnector) isOK = true;
          if (item.isDeleted) isOK = false;

          if (isOK) {
            list.push({
              id: item.id,
              title: item.title,
              connectorType: item.connectorType,
              isTenantConnector: item.isTenantConnector,
              tokenRefreshValidUntil: item.tokenRefreshValidUntil,
              emailCreator: item.emailCreator,
              created: item.created,
              modified: item.modified,
            });
          }
        });
      } catch (err) {
        console.log("ERROR getUserConnectorsByTenantId", err);
      }
    }

    // ===================================================
    // get connector by email
    // ===================================================

    if (list.length === 0) {
      const queryArConnectorsByEmailCreator = gql(`
        query GetConnectorsByEmail {
          queryArConnectorsByEmailCreator(email: "${userEmail}", limit: 5) {
            items {
              id
              tenantId
              isDeleted
              isTenantConnector
              tokenRefreshValidUntil
              connectorType
              title
              emailCreator
              created
              modified
            }
            nextToken
          }
      }`);
      try {
        let awsResult = await this.client.query({
          query: queryArConnectorsByEmailCreator,
          fetchPolicy: "network-only",
        });
        const awsConnectors = awsResult?.data?.queryArConnectorsByEmailCreator;
        awsConnectors?.items.forEach((item) => {
          list.push({
            id: item.id,
            title: item.title,
            connectorType: item.connectorType,
            isTenantConnector: item.isTenantConnector,
            tokenRefreshValidUntil: item.tokenRefreshValidUntil,
            emailCreator: item.emailCreator,
            created: item.created,
            modified: item.modified,
          });
        });
      } catch (err) {
        console.log("ERROR getUserConnectors", err);
      }
    }

    // ===================================================
    // filter and sort the result list
    // ===================================================

    let resultList = [];

    // tenant connector is best
    list.forEach((item) => {
      if (item.isTenantConnector) {
        resultList.push(item);
      }
    });

    // sort by created
    let emailList = list
      .filter((item) => isSameText(item.emailCreator, userEmail))
      .sort((a, b) => {
        return new Date(b.modified) - new Date(a.modified);
      });

    emailList.forEach((item) => {
      if (item.emailCreator === userEmail) {
        let alreadyThere = resultList.find((i) => i.id === item.id);
        !alreadyThere && resultList.push(item);
      }
    });

    return resultList;
  }

  // =================================================================
  //
  // NOTIFICATIONS
  //
  // =================================================================

  async createNotification({
    emailCreator,
    emailReceiver,
    data,
    type,
    header,
    content,
  }) {
    const dataString = JSON.stringify(data);
    const contentString = content ?? "";

    const mutationCreateNotification = gql(`
      mutation CreateArNotification($content: String, $data: AWSJSON) {
        createArNotification(emailCreator: "${emailCreator}", emailReceiver: "${emailReceiver}", type: "${type}", header: "${header}", data: $data, content: $content)
        {
          id
          type
          header
          content
          emailCreator
          emailReceiver
          created
        }
      }`);

    try {
      const newNotification = await this.client.mutate({
        mutation: mutationCreateNotification,
        variables: { content: contentString, data: dataString },
      });
      return newNotification;
    } catch (err) {
      console.log("ERROR createNotification", err);
    }
  }

  processNotificationsNewNotification(data) {
    const newNotification = data.data.onCreateArNotification;
    if (newNotification?.id) {
      // notification list
      setTimeout(() => {
        this.rootStore?.sharing?.setNewNotification(newNotification);
      }, 1000);
    }
  }

  async getUserNotifications(count) {
    const userEmail = this.rootStore.main.userEmail;

    const queryGetUserNotifications = gql(`
      query getArUserNotifications {
        queryArUserNotifications(userEmail: "${userEmail}", limit: 5) {
          items {
            id
            type
            emailCreator
            emailReceiver
            header
            content
            data
            created
          }
          nextToken
        }
    }`);
    try {
      let awsResult = await this.client.query({
        query: queryGetUserNotifications,
      });
      const awsNotifications = awsResult?.data?.queryArUserNotifications;
      this.rootStore.sharing.setNotifications(awsNotifications?.items);
    } catch (err) {
      console.log("ERROR getUserNotifications", err);
    }
  }

  processNotificationsNewSubmit(data) {
    if (!data) return;

    // try {
    // const newSubmit = data.data.onCreateArSubmit;
    // if (statusUpdate?.id && statusUpdate?.status) {
    //   setTimeout(() => {
    //     this.rootStore?.sharing?.updatedInvitationStatusFromNotification(
    //       statusUpdate,
    //     );
    //   }, 1000);
    // }
    // } catch (err) {
    //   console.log("ERROR processNotificationsNewSubmit", err);
    // }
  }

  // ===============================
  //
  // DOMAINS
  //
  // ===============================

  async getUserDomainSettings() {
    const userDomain = this.rootStore.main.userDomain;

    if (!userDomain || userDomain.includes("@")) return;
    if (!userDomain.includes(".")) return;

    // only if it is a concrete domain
    const queryGetDomainSettings = gql(`
      query getDomainSettings {
        getArDomainSettings(id: "${userDomain}") {
          id
          logoUrl
          logoHeight
          companyName
          colorPrimary
          defaultApps
          manifestId
          created
        }
    }`);

    try {
      let awsResult = await this.client.query({
        query: queryGetDomainSettings,
        // fetchPolicy: "network-only",
      });
      if (!awsResult) return;
      let domainSettings = awsResult.data?.getArDomainSettings ?? {};
      if (domainSettings?.defaultApps)
        domainSettings.defaultApps = getAsJson(domainSettings.defaultApps);
      this.rootStore.main.setUserDomainSettings(domainSettings);
      return awsResult;
    } catch (err) {
      console.log("ERROR getUserDomainSettings", err);
    }
  }

  // ===============================
  // Domain Templates
  // ===============================

  async getDomainTemplates() {
    const userDomain = this.rootStore.main.userDomain;
    if (!userDomain) return;

    const queryGetDomainTemplates = gql(`
      query getDomainTemplates {
        queryArDomainTemplatesByDomainId(domainId: "${userDomain}") {
          items {
              created
              description
              domainId
              id
              isArchived
              isDeleted
              modified
              title
              topImageUrl
              userId
            }
        }
    }`);

    try {
      let awsResult = await this.client.query({
        query: queryGetDomainTemplates,
        fetchPolicy: "network-only",
      });
      if (!awsResult) return;
      let domainTemplates =
        awsResult.data?.queryArDomainTemplatesByDomainId ?? {};
      let items = domainTemplates.items ?? [];
      let list = [];
      items.forEach((item) => {
        item.type = "template";
        item.options = "no-cardtype,no-cardcreated";
        list.push(item);
      });

      return list;
    } catch (err) {
      console.log("ERROR getDomainTemplates", err);
    }
  }

  async getDomainTemplate(templateId) {
    if (!templateId) return;

    const getDomainTemplate = gql(`
      query getDomainTemplate {
        getArDomainTemplate(id: "${templateId}") {
          created
          description
          domainId
          id
          data
          modified
          title
          topImageUrl
          userId
        }
    }`);

    try {
      let awsResult = await this.client.query({
        query: getDomainTemplate,
        fetchPolicy: "network-only",
      });
      if (!awsResult) return;
      let item = awsResult.data?.getArDomainTemplate;

      return item;
    } catch (err) {
      console.log("ERROR getDomainTemplate", err);
    }
  }

  async createDomainTemplate(title, template) {
    const domainId = this.rootStore.main.userDomain;
    const userId = this.rootStore.main.getUserEmailOrId();
    const templateTitle = title ?? "New Template";
    const data = JSON.stringify(template);

    try {
      const mutationCreateDomainTemplate = gql(`
      mutation CreateArDomainTemplate($data: AWSJSON) {
        createArDomainTemplate(domainId: "${domainId}", userId: "${userId}", title: "${templateTitle}", data: $data) {
          id
        }
      }`);
      // create a new entry if there was no result
      const createDomainTemplate = await this.client.mutate({
        mutation: mutationCreateDomainTemplate,
        variables: { data: data },
      });
      return createDomainTemplate;
    } catch (err) {
      console.log("ERROR createDomainTemplate", err);
    }
  }

  async deleteDomainTemplate(templateId) {
    try {
      const mutationDeleteDomainTemplate = gql(`
      mutation DeleteArDomainTemplate {
        deleteArDomainTemplate(id: "${templateId}") {
          id
        }
      }`);
      // create a new entry if there was no result
      const deleteDomainTemplate = await this.client.mutate({
        mutation: mutationDeleteDomainTemplate,
      });
      // reload domain templates
      this.rootStore.main.checkDomainTemplates(true);

      return deleteDomainTemplate;
    } catch (err) {
      console.log("ERROR deleteDomainTemplate", err);
    }
  }

  // ===============================
  // Domain Tables
  // ===============================

  async getDomainTables() {
    const userDomain = this.rootStore.main.userDomain;

    if (!userDomain) return;

    const queryGetDomainTables = gql(`
      query getDomainTables {
        queryArDomainTablesByDomainId(domainId: "${userDomain}") {
          items {
              created
              data
              description
              domainId
              id
              isArchived
              isDeleted
              modified
              recordCount
              title
              topImageUrl
              sourceType
              userId
            }
        }
    }`);

    try {
      let awsResult = await this.client.query({
        query: queryGetDomainTables,
        fetchPolicy: "network-only",
      });
      if (!awsResult) return;
      let domainTables = awsResult.data?.queryArDomainTablesByDomainId ?? {};
      let items = domainTables.items ?? [];
      let list = [];
      items.forEach((item) => {
        item.data = getAsJson(item.data);
        list.push(item);
      });

      return list;
    } catch (err) {
      console.log("ERROR getDomainTables", err);
    }
  }

  async getDomainTable(tableId) {
    if (!tableId) return;

    const getGetDomainTable = gql(`
      query getDomainTable {
        getArDomainTable(id: "${tableId}") {
          created
          data
          description
          domainId
          id
          isArchived
          isDeleted
          modified
          recordCount
          title
          topImageUrl
          sourceType
          userId
        }
    }`);

    try {
      let awsResult = await this.client.query({
        query: getGetDomainTable,
        fetchPolicy: "network-only",
      });
      if (!awsResult) return;
      let domainTable = awsResult.data?.getArDomainTable ?? {};
      domainTable.data = getAsJson(domainTable.data);

      return domainTable;
    } catch (err) {
      console.log("ERROR getDomainTable", err);
    }
  }

  async createDomainTable(info) {
    const domainId = this.rootStore.main.userDomain;
    const userId = this.rootStore.main.getUserEmailOrId();
    const sourceType = info?.sourceType ?? "records";
    const title = info?.title ?? "New Table";
    const topImageUrl = info?.topImageUrl ?? "";
    const dataJson = info?.data ?? {};
    const data = JSON.stringify(dataJson);

    try {
      const mutationCreateDomainTable = gql(`
      mutation CreateArDomainTable($data: AWSJSON) {
        createArDomainTable(domainId: "${domainId}", userId: "${userId}", title: "${title}", topImageUrl: "${topImageUrl}", sourceType: "${sourceType}", data: $data) {
          id
        }
      }`);
      // create a new entry if there was no result
      const createDomainTable = await this.client.mutate({
        mutation: mutationCreateDomainTable,
        variables: { data: data },
      });
      const tableId = createDomainTable?.data?.createArDomainTable?.id;
      console.log("INFO createDomainTable", tableId);
      return tableId;
    } catch (err) {
      console.log("ERROR createDomainTable", err);
    }
  }

  async updateDomainTable(info) {
    const id = info?.id;
    const sourceType = info?.sourceType;
    const title = info?.title ?? "New Table";
    if (!id || !title || !sourceType) return;

    const description = info?.description ?? "";
    const topImageUrl = info?.topImageUrl ?? "";
    const dataJson = info?.data ?? {};
    const data = JSON.stringify(dataJson);
    try {
      const mutationUpdateDomainTable = gql(`
      mutation UpdateArDomainTable($data: AWSJSON) {
        updateArDomainTable(id: "${id}", title: "${title}", description: "${description}", topImageUrl: "${topImageUrl}", sourceType: "${sourceType}", data: $data) {
          id
        }
      }`);
      // create a new entry if there was no result
      const updateDomainTable = await this.client.mutate({
        mutation: mutationUpdateDomainTable,
        variables: { data: data },
      });
      return updateDomainTable;
    } catch (err) {
      console.log("ERROR updateDomainTable", err);
    }
  }

  async deleteDomainTable(tableId) {
    try {
      const mutationDeleteDomainTable = gql(`
      mutation DeleteArDomainTable {
        deleteArDomainTable(id: "${tableId}") {
          id
        }
      }`);
      // create a new entry if there was no result
      const deleteDomainTable = await this.client.mutate({
        mutation: mutationDeleteDomainTable,
      });
      return deleteDomainTable;
    } catch (err) {
      console.log("ERROR deleteDomainTable", err);
    }
  }

  async createDomainTableRecord(
    domainTableId,
    fileId = "",
    appId = "",
    appName = "",
    info = {}
  ) {
    if (!domainTableId) return;

    const domainId = this.rootStore.main.userDomain;
    const userId = this.rootStore.main.getUserEmailOrId();
    const recordType = "record";
    const dataJson = info;
    const data = JSON.stringify(dataJson);

    try {
      const mutationCreateDomainTableRecord = gql(`
      mutation CreateArDomainTableRecord($data: AWSJSON) {
        createArDomainTableRecord(
        domainTableId: "${domainTableId}", 
        domainId: "${domainId}", 
        userId: "${userId}", 
        fileId: "${fileId}", 
        appId: "${appId}", 
        appName: "${appName}", 
        recordType: "${recordType}", 
        data: $data) {
          id
        }
      }`);
      // create a new entry if there was no result
      const createDomainTableRecord = await this.client.mutate({
        mutation: mutationCreateDomainTableRecord,
        variables: { data: data },
      });
      return createDomainTableRecord;
    } catch (err) {
      console.log("ERROR createDomainTableRecord", err);
    }
  }

  async getDomainTableRecords(domainTableId) {
    if (!domainTableId) return;

    const queryGetDomainTableRecords = gql(`
    query getDomainTableRecords {
      queryArDomainTableRecordsByDomainTableId(domainTableId: "${domainTableId}") {
        items {
          id
          domainTableId
          invitationId
          userId
          fileId
          appId
          appName
          recordType
          data
          created
        }
      }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetDomainTableRecords,
        fetchPolicy: "network-only",
      });
      const tableRecords =
        awsResult?.data?.queryArDomainTableRecordsByDomainTableId?.items;
      if (!tableRecords) return [];

      let list = [];
      tableRecords
        .sort((a, b) => {
          return new Date(b.created) - new Date(a.created);
        })
        .forEach((item) => {
          let record = {};
          if (isJsonString(item.data)) {
            let values = getAsJson(item.data);
            values.forEach((value) => {
              if (value.label) {
                record[value.label] = value.value;
              }
            });

            record["app"] = item.appName;
            record["userId"] = item.userId;
            record["created"] = item.created;
            record["id"] = item.id;
            record["key"] = item.id;
            record["invitationId"] = item.invitationId;
            record["fileId"] = item.fileId;
            record["appId"] = item.appId;
            list.push(record);
          }
        });
      return list;
    } catch (err) {
      console.log("ERROR getDomainTableRecords", err);
      return [];
    }
  }

  async deleteDomainTableRecords(domainTableId, idList) {
    if (!domainTableId) return;
    if (!idList || idList.length === 0) return;
    let isError = false;

    const ids = idList.toString();
    try {
      const mutationDeleteDomainTableRecordsByIdList = gql(`
        mutation DeleteArDomainTableRecordsByIdList($ids: String) {
        deleteArDomainTableRecordsByIdList(domainTableId: "${domainTableId}", ids: $ids) {
          items {
              id
          }
        }
      }`);
      await this.client.mutate({
        mutation: mutationDeleteDomainTableRecordsByIdList,
        variables: { ids: ids },
      });
      isError = false;
    } catch (err) {
      console.log("ERROR deleteDomainTableRecords", err);
      isError = true;
    }
    return { isError: isError };
  }

  // ===============================
  //
  // INVITATIONS
  //
  // ===============================

  async createUserInvitation(
    shareId,
    documentId,
    fileId,
    connectorId,
    elementId,
    shareValues,
    shareUrl,
    shareType,
    linkType,
    pricing,
    activeMonetize,
    cacheJson = {}
  ) {
    const name = shareValues.title ? shareValues.title : "[no title]";
    const type = shareValues.type;
    const folderId = shareValues.folderId;
    const manifestId = this.rootStore.main.userDomainSettings?.manifestId;
    const limitElementCount = this.rootStore.main.limitElementCount ?? 15;
    const licenseType = this.rootStore.main.licenseType ?? "free";
    let licenseCheck = this.rootStore.main.licenseCheck;

    const url = shareUrl;
    let emailSender = this.rootStore.main.userEmail;
    if (!emailSender || emailSender.length < 5) {
      let publicId = this.rootStore.main.getPublicUserId();
      emailSender = publicId ?? "local";
    }
    let emailReceiver =
      linkType === "private" ? shareValues.recipient?.toLowerCase() : "";
    if (!emailReceiver) emailReceiver = "undefined";

    // get the receivers list
    const emailReceiversForJson = shareValues.recipients ?? [];
    let receivers = [];
    emailReceiversForJson &&
      emailReceiversForJson.forEach((emailReceiver, index) => {
        receivers.push(emailReceiver.email);
      });
    let dataJson = {
      elementId: elementId,
      fileId: fileId,
      tenantId: this.rootStore.main.userTenantId,
      instructions: shareValues.instructions,
      allowForwarding: shareValues.allowForwarding,
      allowedEmailsAndDomains: shareValues.allowedEmailsAndDomains,
      startFullscreen: shareValues.startFullscreen,
      password: shareValues.password,
      teamFolder: shareValues.teamFolder,
      shareType: shareType,
      linkType: linkType,
      limitElementCount: limitElementCount,
      licenseType: licenseType,
      licenseCheck: licenseCheck,
      // write
      writeEnforceComments: shareValues.writeEnforceComments,
      writeEnforceStaging: shareValues.writeEnforceStaging,
      priceId: pricing?.id,
      priceWeek: pricing?.week,
      priceMonth: pricing?.month,
      trial: shareValues.trial,
    };
    if (manifestId) {
      dataJson.manifestId = manifestId;
    }
    if (isValidDate(shareValues.validuntil?._d)) {
      dataJson.validUntil = shareValues.validuntil?._d;
    }
    if (shareValues.topImageUrl) {
      dataJson.topImageUrl = shareValues.topImageUrl;
    }
    const data = JSON.stringify(dataJson);
    const cacheInfo = JSON.stringify(cacheJson);
    const emailReceivers = receivers; //JSON.stringify(receivers);

    const mutationAddInvitation = gql(`
      mutation CreateArInvitation($data: AWSJSON, $cacheInfo: AWSJSON, $emailReceivers: [String]) {
        createArInvitation(id: "${shareId}", name: "${name}", url: "${url}", emailSender: "${emailSender}", emailReceiver: "${emailReceiver}", emailReceivers: $emailReceivers, type: "${type}", data: $data, cacheInfo: $cacheInfo, documentId: "${documentId}", connectorId: "${connectorId}", folderId: "${folderId}") {
          id
          name
          emailReceiver
          type
          status
          data
          cacheInfo
          created
        }
      }`);
    try {
      // create a new entry if there was no result
      const newInvitationData = await this.client.mutate({
        mutation: mutationAddInvitation,
        variables: {
          data: data,
          cacheInfo: cacheInfo,
          emailReceivers: emailReceivers,
        },
      });
      // update invitation lists
      let newInvitation = newInvitationData.data?.createArInvitation;
      if (newInvitation) {
        const invitationData = getAsJson(newInvitation.data);
        newInvitation.data = invitationData;
        this.rootStore.sharing.addInvitationsOutbound(newInvitation);
        this.rootStore.sharing.getTeamItems();
      }

      // write a notification
      if (emailReceiver !== "undefined") {
        this.createNotification({
          emailCreator: emailSender,
          emailReceiver: emailReceiver,
          data: {
            invitationId: shareId,
            elementId: elementId,
            documentId: documentId,
            shareType: shareType,
          },
          type: "new-invitation",
          header: "new invitation: " + name,
          content: shareValues.instructions,
        });
      }
      return true;
    } catch (err) {
      console.log("ERROR addInvitation", err);
      return false;
    }
  }

  async updateUserInvitation(shareId, shareValues, shareData, linkType) {
    const name = shareValues.title ? shareValues.title : "[no title]";
    const emailReceiver =
      linkType === "private" ? shareValues.recipient?.toLowerCase() : undefined;
    const emailReceiversJson = shareValues.recipients;
    let receivers = [];
    emailReceiversJson &&
      emailReceiversJson.forEach((emailReceiver, index) => {
        receivers.push(emailReceiver.email);
      });

    let dataJson = jsonCopy(shareData) ?? {};
    // set the actual license information
    dataJson.limitElementCount = this.rootStore.main.limitElementCount ?? 15;
    dataJson.licenseType = this.rootStore.main.licenseType ?? "free";
    dataJson.licenseCheck = this.rootStore.main.licenseCheck;
    dataJson.allowedEmailsAndDomains = shareValues.allowedEmailsAndDomains;

    const data = JSON.stringify(dataJson);
    const emailReceivers = receivers;

    const mutationUpdateInvitation = gql(`
      mutation UpdateArInvitation($data: AWSJSON, $emailReceivers: [String]) {
        updateArInvitation(id: "${shareId}", name: "${name}", emailReceiver: "${emailReceiver}", emailReceivers: $emailReceivers, data: $data) {
          id
          name
          emailReceiver
          emailReceivers
          data
          modified
        }
      }`);

    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationUpdateInvitation,
        variables: { data: data, emailReceivers: emailReceivers },
      });
      // write a notification
      // this.createNotification({
      //   emailCreator: emailSender,
      //   emailReceiver: emailReceiver,
      //   data: {
      //     invitationId: shareId,
      //     elementId: elementId,
      //     documentId: documentId,
      //     shareType: shareType,
      //   },
      //   type: "new-invitation",
      //   header: "new invitation: " + name,
      //   content: shareValues.instructions,
      // });
      return true;
    } catch (err) {
      console.log("ERROR updateInvitation", err);
      return false;
    }
  }

  async updateUserInvitationStatus(sharingInfo, status, comment) {
    if (!sharingInfo) return;

    const emailCreator = this.rootStore.main.userEmail;
    const mutationUpdateStatus = gql(`
      mutation UpdateArInvitationStatus($comment: String) {
        updateArInvitationStatus(id: "${sharingInfo.id}", status: "${status}", comment: $comment, emailCreator: "${emailCreator}") {
          id
          name
          status
          emailSender
          modified
        }
      }`);

    try {
      await this.client.mutate({
        mutation: mutationUpdateStatus,
        variables: { comment: comment },
      });
      // write a notification
      this.createNotification({
        emailCreator: emailCreator,
        emailReceiver: sharingInfo.emailSender ?? "",
        data: {
          invitationId: sharingInfo.id,
          documentId: sharingInfo.documentId,
          elementId: sharingInfo.data?.elementId,
          status: status,
          status_old: sharingInfo.status,
        },
        type: "update-invitationstatus",
        header:
          "status updated: " +
          this.rootStore.sharing.getStatusDescription(sharingInfo.status) +
          " -> " +
          this.rootStore.sharing.getStatusDescription(status),
        content: comment ?? "",
      });

      return true;
    } catch (err) {
      console.log("ERROR updateUserInvitation", err);
      return false;
    }
  }

  async updateUserInvitationIsArchived(shareId, isArchived) {
    const mutationUpdateArchived = gql(`
      mutation UpdateArInvitationIsArchived {
        updateArInvitationIsArchived(id: "${shareId}") {
          id
          name
          status
          emailSender
          isDeleted
          isArchived
          modified
        }
      }`);

    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationUpdateArchived,
      });
      return true;
    } catch (err) {
      console.log("ERROR updateUserInvitationIsArchived", err);
      return false;
    }
  }

  async updateUserInvitationIsDeleted(shareId, isDeleted) {
    const mutationUpdateDeleted = gql(`
      mutation UpdateArInvitationIsDeleted {
        updateArInvitationIsDeleted(id: "${shareId}") {
          id
          name
          status
          emailSender
          isDeleted
          isArchived
          modified
        }
      }`);

    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationUpdateDeleted,
      });
      return true;
    } catch (err) {
      console.log("ERROR updateUserInvitationIsDeleted", err);
      return false;
    }
  }

  async updateUserInvitationCacheData(
    shareId,
    cacheData,
    cacheVersion,
    cacheStorage
  ) {
    const mutationUpdateInvitationCacheData = gql(`
      mutation UpdateArInvitationCacheData {
        updateArInvitationCacheData(id: "${shareId}", cacheData: "${cacheData}", cacheVersion: "${cacheVersion}") {
          id
          modified
        }
      }`);
    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationUpdateInvitationCacheData,
      });
      return true;
    } catch (err) {
      console.log("ERROR updateUserInvitationCacheData", err);
      return false;
    }
  }

  async updateUserInvitationDesignInfo(shareId, designInfo) {
    let designInfoJson = jsonCopy(designInfo);

    const designData = JSON.stringify(designInfoJson);

    const mutationUpdateInvitationDesignInfo = gql(`
      mutation UpdateArInvitationDesignInfo($designData: AWSJSON) {
        updateArInvitationDesignInfo(id: "${shareId}", designInfo: $designData) {
          id
          modified
        }
      }`);

    try {
      // create a new entry if there was no result
      await this.client.mutate({
        mutation: mutationUpdateInvitationDesignInfo,
        variables: { designData: designData },
      });
      return true;
    } catch (err) {
      console.log("ERROR updateUserInvitationDesignInfo", err);
      return false;
    }
  }

  async getInvitationDetail(shareId, type) {
    if (!shareId) return true;
    // ====================================================
    // is it a shared Area?
    // ====================================================
    if (shareId.includes("ar_area_") || shareId.startsWith("ar_")) {
      let invitation = {};
      let arId = shareId;
      let password = "";
      // is the shareId perhaps encrypted
      if (shareId.startsWith("ar_")) {
        let compressedJson = shareId.replace("ar_", "");
        const linkJson = base64UrlToJson(compressedJson);
        if (linkJson?.arId) arId = linkJson.arId;
        if (linkJson?.password) password = linkJson.password;
        if (linkJson?.emailReceiver)
          invitation.emailReceiver = linkJson.emailReceiver;
        if (linkJson?.emailReceivers)
          invitation.emailReceivers = linkJson.emailReceivers;
      }
      // get the password and emailReceiver

      invitation.id = arId;
      invitation.cacheData =
        "https://excel-inventory.airrange.io/api/getpackage?areaId=" + arId;
      invitation.cacheVersion = 1;
      invitation.documentId = arId;
      invitation.connectorId = null;
      invitation.name = "..."; //"Shared Data";
      invitation.emailSender = "";
      invitation.isDeleted = false;
      invitation.isArchived = false;
      invitation.data = {
        linkType: "public",
        removeScenarios: true,
        startFullscreen: true,
      };
      if (password) invitation.data.password = password;
      invitation.type = "calculation";
      invitation.url = "";
      return invitation;
    }

    const queryGetInvitation = gql(`
      query getInvitation {
        getArInvitation(id: "${shareId}") {
          id
          documentId
          connectorId
          name
          emailReceiver
          emailReceivers
          emailSender
          type
          status
          created
          data
          url
          connectorInfo {
            tokenValidUntil
            tokenAccess
          }
          cacheInfo
          cacheData
          cacheVersion
          designInfo
          isDeleted
          isArchived
        }
    }`);

    try {
      // add an invitation action
      this.createInvitationAction(shareId, type ? type : "detail");
      // get the invitation detail
      let awsResult = await this.client.query({
        query: queryGetInvitation,
        fetchPolicy: "network-only",
      });
      let arInvitation = awsResult?.data?.getArInvitation;
      let invitation = jsonCopy(arInvitation);
      if (
        invitation?.connectorId === "undefined" ||
        invitation?.connectorId === ""
      )
        invitation.connectorId = null;
      let userEmail = this.rootStore.main.userEmail;
      let isPublic = false;
      let isValid = true;
      let errorMessage = "";
      let errorAdditional = null;

      if (!invitation) return null;
      const isSender = isSameText(invitation.emailSender, userEmail);
      let allowedEmailsAndDomainsList = [];
      const isUndefined = invitation.emailReceiver === "undefined";
      // convert strings in json
      if (isJsonString(invitation.data)) {
        invitation.data = getAsJson(invitation.data);
        const allowedEmailsAndDomains = invitation.data.allowedEmailsAndDomains;
        allowedEmailsAndDomainsList = allowedEmailsAndDomains?.split(",") ?? [];
        if (invitation.data.validUntil) {
          invitation.data.validUntil = new Date(invitation.data.validUntil);
        }
        if (!invitation.data.limitElementCount) {
          invitation.data.limitElementCount = 15; // limit for free usage
        }
        if (invitation.data.isPayedContent === true) {
          invitation.data.limitTrialDays = 3; // limit free trial days
          invitation.data.paymentTier = "tier_1"; // limit free trial days
        }

        if (invitation.data.shareType === "public") isPublic = true;
      }
      if (isJsonString(invitation.designInfo)) {
        invitation.designInfo = getAsJson(invitation.designInfo);
      }
      if (isJsonString(invitation.cacheInfo)) {
        invitation.cacheInfo = getAsJson(invitation.cacheInfo);
      }
      // ==============================================
      // valid until
      // ==============================================
      const validUntil = invitation.data.validUntil;
      if (isValidDate(validUntil) && !isSender) {
        if (isInThePast(validUntil)) errorMessage = "invitation-expired";
      }
      // ==============================================
      // tenantId
      // ==============================================
      const linkType = invitation.data.linkType;
      if (linkType === "organization") {
        const targetTenantId = invitation.data.tenantId;
        if (targetTenantId !== this.rootStore.main.userTenantId) {
          errorMessage = "invitation-wrongorganization";
        }
      }
      // ==============================================
      // deleted & final flag
      // ==============================================
      const isDeleted = invitation.isDeleted ?? false;
      if (isDeleted) errorMessage = "invitation-deleted";
      // status mode
      const isStatusOk = !["cancelled"].includes(invitation.status) || isSender;
      if (!isStatusOk) errorMessage = "invitation-status";

      // ==============================================
      // check the Receiver
      // ==============================================
      let isReceiver =
        isSameText(invitation.emailReceiver, userEmail) ||
        invitation.emailReceivers?.includes(userEmail);

      // is it the right person
      let isReceiverOk = isReceiver || isSender || isPublic || isUndefined;
      if (isUndefined && allowedEmailsAndDomainsList?.length > 0)
        isReceiverOk = false;
      if (!isReceiverOk) {
        // look for additional rules
        allowedEmailsAndDomainsList?.forEach((allowedEmail) => {
          let emailOrDomain = allowedEmail?.replaceAll("*", "");
          if (userEmail.includes(emailOrDomain)) isReceiverOk = true;
        });

        if (!isReceiverOk) {
          errorMessage = "invitation-wrongreceiver";
          errorAdditional = invitation.emailReceiver ?? "";
          if (
            allowedEmailsAndDomainsList &&
            allowedEmailsAndDomainsList.length > 0
          ) {
            if (!isUndefined)
              allowedEmailsAndDomainsList.push(invitation.emailReceiver);
            errorAdditional = allowedEmailsAndDomainsList.toString();
          }
          errorAdditional = errorAdditional?.replaceAll(",", ", ");
        }
      }

      // ==============================================
      // manage Errors
      // ==============================================
      if (errorMessage.length > 0) isValid = false;
      invitation.isValid = !isDeleted && isStatusOk && isReceiverOk && isValid;
      if (errorMessage) invitation.errorMessage = errorMessage;
      if (errorAdditional) invitation.errorAdditional = errorAdditional;
      // mark an invited invitation as opened
      if (invitation.status === "invited") {
        if (
          !invitation.emailReceiver ||
          invitation.emailReceiver === "undefined" ||
          isSameText(invitation.emailReceiver, userEmail)
        ) {
          // mark it as opened
          await this.rootStore.sharing.updateInvitationStatus(
            invitation,
            "opened",
            ""
          );
          invitation.status = "opened";
        }
      }
      return invitation;
    } catch (err) {
      console.log("ERROR getInvitationDetail", err);
    }
  }

  async getInvitationUrl(shareId) {
    if (!shareId) return true;
    const queryGetInvitation = gql(`
      query getInvitation {
        getArInvitation(id: "${shareId}") {
          id
          type
          url
        }
    }`);
    try {
      let awsResult = await this.client.query({ query: queryGetInvitation });
      let url = awsResult?.data?.getArInvitation?.url;
      let type = awsResult?.data?.getArInvitation?.type;
      let prefix = "element";
      if (type === "workbook") prefix = "workbook";

      // add an invitation action
      // this.createInvitationAction(shareId, "geturl");

      return "/" + prefix + "/" + url;
    } catch (err) {
      console.log("ERROR getInvitationUrl", err);
    }
  }

  async getDocumentInvitations(documentId, elementId) {
    if (!documentId) return false;

    const queryGetDocumentInvitations = gql(`
    query getDocumentInvitations {
      queryArInvitationsByDocumentId(documentId: "${documentId}") {
        items {
          id
          name
          emailReceiver
          data
          cacheData
          type
          status
          created
          isDeleted
          isArchived
    }
      }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetDocumentInvitations,
        fetchPolicy: "network-only",
      });
      const documentInvitations =
        awsResult?.data?.queryArInvitationsByDocumentId?.items;
      // awsResult?.data?.queryArInvitationsByDocumentId?.items.filter(
      //   (item) => !item.isArchived && !item.isDeleted,
      // );
      if (!documentInvitations) return [];
      // create a shared elements list
      let shares = [];
      documentInvitations.forEach((invitation) => {
        const data = getAsJson(invitation.data);
        let bOK = data?.elementId || invitation.type === "workbook";
        // do we want to filter for the elementId = invitations per app
        if (bOK && elementId) {
          bOK = data.elementId === elementId;
        }

        if (bOK) {
          shares.push({
            shareId: invitation.id,
            key: invitation.id,
            name: invitation.name,
            type: invitation.type,
            elementId: data.elementId,
            sharedWith: invitation.emailReceiver,
            status: invitation.status,
            created: invitation.created,
            createdDate: new Date(invitation.created),
            data: data,
            cacheData: invitation.cacheData,
          });
        }
      });
      return shares.sort((a, b) => {
        return b.createdDate - a.createdDate;
      });
    } catch (err) {
      console.log("ERROR getDocumentInvitations", err);
      return [];
    }
  }

  async getFolderInvitations(folderId, type) {
    if (!folderId) return false;

    const queryGetFolderInvitations = gql(`
    query getFolderInvitations {
      queryArInvitationsByFolderId(folderId: "${folderId}") {
        items {
          id
          folderId
          name
          data
          type
          status
          emailSender
          designInfo
          created
          isDeleted
          isArchived
        }
      }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetFolderInvitations,
      });
      const folderInvitations =
        awsResult?.data?.queryArInvitationsByFolderId?.items;
      // awsResult?.data?.queryArInvitationsByFolderId?.items.filter(
      //   (item) => !item.isArchived && !item.isDeleted,
      // );
      if (!folderInvitations) return [];
      // create a shared elements list
      let shares = [];
      folderInvitations.forEach((invitation) => {
        const data = getAsJson(invitation.data);
        const designInfo = getAsJson(invitation.designInfo);

        if (data?.elementId || invitation.type === "workbook") {
          shares.push({
            id: invitation.id,
            shareId: invitation.id,
            folderId: invitation.folderId,
            name: invitation.name,
            type: invitation.type,
            elementId: data.elementId,
            status: invitation.status,
            emailSender: invitation.emailSender,
            created: invitation.created,
            data: data,
            designInfo: designInfo,
          });
        }
      });
      return shares;
    } catch (err) {
      console.log("ERROR getFolderInvitations", err);
      return [];
    }
  }

  async createInvitationAction(invitationId, type) {
    let user = this.rootStore.main.userEmail;
    let publicUserId = this.rootStore.main.getPublicUserId();
    user = user ? user + "_" + publicUserId : publicUserId;

    const location = this.rootStore.main.userEmail;
    const tenantId = this.rootStore.main.userTenantId;

    const mutationCreateArInvitationAction = gql(`
        mutation CreateInvitationAction {
          createArInvitationAction(invitationId: "${invitationId}", tenantId: "${tenantId}", type: "${type}", location: "${location}", user: "${user}") {
            id
          }

      }`);

    try {
      // create a new entry if there was no result
      this.client.mutate({
        mutation: mutationCreateArInvitationAction,
      });
      return true;
    } catch (err) {
      console.log("ERROR createInvitationAction", err);
      return false;
    }
  }

  async getInvitationActions(invitationId, type) {
    if (!invitationId) return false;

    const queryGetInvitationActions = gql(`
    query getInvitationActions {
      queryArInvitationActionsByInvitationId(invitationId: "${invitationId}") {
        items {
          created
          user
          type
        }
      }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetInvitationActions,
        fetchPolicy: "network-only",
      });
      const invitationActions =
        awsResult?.data?.queryArInvitationActionsByInvitationId?.items;
      if (!invitationActions) return [];
      // get the analytics for the chart
      let actions = [];
      invitationActions
        .sort((a, b) => {
          return new Date(a.created) - new Date(b.created);
        })
        .forEach((action) => {
          if (action.created && action.type === type) {
            actions.push({
              created: format(new Date(action.created), "yyyy-MM-dd"),
              user: action.user,
            });
          }
        });
      // grouped by Date
      const byDate = await getGroupByData(actions, "created");

      return byDate;
    } catch (err) {
      console.log("ERROR getInvitationActions", err);
      return [];
    }
  }

  // ==============================================
  // Pages
  // ==============================================

  async getInvitationForPage(invitationId) {
    if (!invitationId) return true;

    const queryGetInvitation = gql(`
      query getInvitation {
        getArInvitation(id: "${invitationId}") {
          id
          name
          data
          type
          status
          emailSender
          designInfo
          created
          isDeleted
          isArchived
        }
    }`);
    let invitation;
    try {
      let awsResult = await this.client.query({ query: queryGetInvitation });
      invitation = awsResult?.data?.getArInvitation;

      if (invitation) {
        const data = getAsJson(invitation.data);
        const designInfo = getAsJson(invitation.designInfo);

        invitation.data = data;
        invitation.designInfo = designInfo;
        invitation.elementId = data?.elementId;
        invitation.shareId = invitation.id;
      }
    } catch (err) {
      console.log("ERROR getInvitationForPage", err);
    }
    return invitation;
  }

  async getCreatorInvitations(userId, type) {
    if (!userId) return false;
    let userEmail = userId;

    const queryArInvitationsByEmailSender = gql(`
    query queryArInvitationsByEmailSender {
      queryArInvitationsByEmailSender(email: "${userEmail}") {
        items {
          id
          folderId
          name
          data
          type
          status
          emailSender
          designInfo
          created
        }
      }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryArInvitationsByEmailSender,
      });
      const creatorInvitations =
        awsResult?.data?.queryArInvitationsByEmailSender?.items;
      // awsResult?.data?.queryArInvitationsByEmailSender?.items.filter(
      //   (item) => !item.isArchived && !item.isDeleted,
      // );
      if (!creatorInvitations) return [];
      // create a shared elements list
      let shares = [];
      creatorInvitations.forEach((invitation) => {
        const data = getAsJson(invitation.data);
        const designInfo = getAsJson(invitation.designInfo);
        let isOK =
          data.linkType === "public" && invitation.type === "calculation";

        if (isOK && type === "personalpage") {
          isOK = data.personalPage === true;
        }
        // if (data?.elementId || invitation.type === "workbook") {
        isOK &&
          shares.push({
            id: invitation.id,
            shareId: invitation.id,
            folderId: invitation.folderId,
            name: invitation.name,
            type: invitation.type,
            elementId: data.elementId,
            status: invitation.status,
            emailSender: invitation.emailSender,
            created: invitation.created,
            data: data,
            designInfo: designInfo,
          });
        // }
      });
      return shares;
    } catch (err) {
      console.log("ERROR getCreatorInvitations", err);
      return [];
    }
  }

  // ==============================================
  // Protocol
  // ==============================================

  async createInvitationProtocol(invitationId, dataJson, title = "") {
    const type = "detail";
    const user =
      this.rootStore.main.userEmail ?? this.rootStore.main.getPublicUserId();
    const storeData = dataJson;
    const data = JSON.stringify(storeData);

    const mutationCreateArInvitationProtocol = gql(`
        mutation CreateInvitationProtocol($data: AWSJSON) {
          createArInvitationProtocol(invitationId: "${invitationId}", type: "${type}", title: "${title}", user: "${user}", data: $data) {
            id
          }
      }`);

    try {
      // create a new entry
      this.client.mutate({
        mutation: mutationCreateArInvitationProtocol,
        variables: { data: data },
      });
      return true;
    } catch (err) {
      console.log("ERROR createInvitationProtocol", err);
      return false;
    }
  }

  // ==============================================
  // Scenarios
  // ==============================================

  async createInvitationScenario(invitationId, values, dataJson) {
    const title = values.title ?? "[none]";
    const description = values.description ?? "";
    const type = values.type ?? "scenario";
    const accessType = values.accessType ?? "personal";
    let emailSender = this.rootStore.main.userEmail;
    const tenantId = this.rootStore.main.userTenantId;
    const storeData = { scenarioData: dataJson };
    const data = JSON.stringify(storeData);
    // const dataSize = getSizeInBytes(data);
    // const compressedData = compressJson(data, "v2"); // encodeduri because of Safari
    // const dataSizeCompressed = getSizeInBytes(compressedData);

    if (!emailSender) emailSender = this.rootStore.main.getPublicUserId();

    const mutationCreateArInvitationScenario = gql(`
        mutation CreateInvitationScenario($data: AWSJSON) {
          createArInvitationScenario(invitationId: "${invitationId}", title: "${title}", description: "${description}", tenantId: "${tenantId}", type: "${type}", accessType: "${accessType}", emailSender: "${emailSender}", data: $data) {
            id
          }
      }`);

    try {
      // create a new entry
      this.client.mutate({
        mutation: mutationCreateArInvitationScenario,
        variables: { data: data },
      });
      return true;
    } catch (err) {
      console.log("ERROR createInvitationScenario", err);
      return false;
    }
  }

  async updateInvitationScenario(invitationId, scenarioId, dataJson) {
    const storeData = { scenarioData: dataJson };
    const data = JSON.stringify(storeData);
    const mutationUpdateArInvitationScenario = gql(`
        mutation UpdateInvitationScenario($data: AWSJSON) {
          updateArInvitationScenario(invitationId: "${invitationId}", id: "${scenarioId}", data: $data) {
            id
          }
      }`);

    try {
      // create a new entry
      this.client.mutate({
        mutation: mutationUpdateArInvitationScenario,
        variables: { data: data },
      });
      return true;
    } catch (err) {
      console.log("ERROR updateInvitationScenario", err);
      return false;
    }
  }

  async deleteInvitationScenario(invitationId, scenarioId) {
    try {
      const mutationDeleteInvitationScenario = gql(`
      mutation DeleteArInvitationScenario {
        deleteArInvitationScenario(invitationId: "${invitationId}", id: "${scenarioId}") {
          id
        }
      }`);
      // create a new entry if there was no result
      const deleteScenario = await this.client.mutate({
        mutation: mutationDeleteInvitationScenario,
      });
      return deleteScenario;
    } catch (err) {
      console.log("ERROR deleteInvitationScenario", err);
    }
  }

  async getInvitationScenario(invitationId, scenarioId) {
    if (!invitationId || !scenarioId) return false;

    const GetInvitationScenario = gql(`
     query GetInvitationScenario {
       getArInvitationScenario(invitationId: "${invitationId}", id: "${scenarioId}") {
         id
         title
         type
         data
       }
      }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: GetInvitationScenario,
      });
      let data;
      let elementValues = [];
      let invitationScenario = awsResult?.data?.getArInvitationScenario ?? {};
      // convert strings in json
      if (isJsonString(invitationScenario.data)) {
        data = getAsJson(invitationScenario.data);
        elementValues = data.scenarioData?.elementValues ?? [];
      }
      let result = {
        id: invitationScenario.id,
        invitationId: invitationId,
        title: invitationScenario.title,
        type: invitationScenario.type ?? "scenario",
        elementValues: elementValues,
      };

      return result;
    } catch (err) {
      console.log("ERROR getInvitationScenario", err);
      return [];
    }
  }

  async getInvitationScenarios(invitationId, type) {
    if (!invitationId) return false;

    const queryGetInvitationScenarios = gql(`
    query getInvitationScenarios {
      queryArInvitationScenariosByInvitationId(invitationId: "${invitationId}") {
        items {
          id
          title
          description
          type
          accessType
          emailSender
          created
        }
      }
    }`);

    try {
      console.time("getInvitationScenarios");
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetInvitationScenarios,
        fetchPolicy: "network-only",
      });
      const invitationScenarios =
        awsResult?.data?.queryArInvitationScenariosByInvitationId?.items;
      if (!invitationScenarios) {
        console.log("INFO getInvitationScenarios count is null");
        return [];
      }
      // get the analytics for the chart
      let items = [];
      let userEmail = this.rootStore.main.userEmail;
      let userPublicId = this.rootStore.main.getPublicUserId();
      if (!userEmail) userEmail = userPublicId;
      console.log(
        "getInvitationScenarios",
        invitationId,
        userEmail,
        invitationScenarios
      );

      invitationScenarios
        .sort((a, b) => {
          return new Date(b.created) - new Date(a.created);
        })
        .forEach((item) => {
          let isOK = item.created;
          if (isOK && item.accessType === "personal" && type !== "all") {
            isOK = isSameText(item.emailSender, userEmail);
          }

          if (isOK) {
            items.push({
              id: item.id,
              invitationId: invitationId,
              title: item.title,
              description: item.description,
              type: item.type ?? "scenario",
              accessType: item.accessType,
              emailSender: item.emailSender,
              created: item.created,
            });
          }
        });
      console.timeEnd("getInvitationScenarios");
      console.log("getInvitationScenarios count", items?.length);
      return items;
    } catch (err) {
      console.log("ERROR getInvitationScenarios", err);
      return [];
    }
  }

  async getInvitationScenariosByUser(invitationId, type) {
    if (!invitationId) return false;
    let emailSender = this.rootStore.main.userEmail;
    let userPublicId = this.rootStore.main.getPublicUserId();
    if (!emailSender) emailSender = userPublicId;
    const queryGetInvitationScenarios = gql(`
    query getInvitationScenarios {
      queryArInvitationScenariosByInvitationIdAndEmailSender(invitationId: "${invitationId}", emailSender: "${emailSender}") {
        items {
          id
          title
          description
          type
          accessType
          emailSender
          created
        }
      }
    }`);

    try {
      console.time("getInvitationScenariosByUser");
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetInvitationScenarios,
        fetchPolicy: "network-only",
      });
      const invitationScenarios =
        awsResult?.data?.queryArInvitationScenariosByInvitationIdAndEmailSender
          ?.items;
      if (!invitationScenarios) {
        console.log("INFO getInvitationScenarios count is null");
        return [];
      }
      // get the analytics for the chart
      let items = [];

      invitationScenarios
        .sort((a, b) => {
          return new Date(b.created) - new Date(a.created);
        })
        .forEach((item) => {
          let isOK = item.created;
          if (isOK && item.accessType === "personal" && type !== "all") {
            isOK = true;
          }

          if (isOK) {
            items.push({
              id: item.id,
              invitationId: invitationId,
              title: item.title,
              description: item.description,
              type: item.type ?? "scenario",
              accessType: item.accessType,
              emailSender: item.emailSender,
              created: item.created,
            });
          }
        });
      console.timeEnd("getInvitationScenariosByUser");
      return items;
    } catch (err) {
      console.log("ERROR getInvitationScenariosByUser", err);
      return [];
    }
  }

  // ==============================================
  //
  // Configurations
  //
  // ==============================================

  async createDocumentConfiguration(
    documentId,
    fileId = "",
    type = "calculation",
    title = "New app",
    topImageUrl = null,
    jsonData = null
  ) {
    const userId = this.rootStore.main.getUserEmailOrId();
    let storeDocumentId = documentId;
    // summary
    const editAreas = jsonData?.editAreas ?? [];
    const spreadAreas = jsonData?.spreadAreas ?? [];
    let summary = JSON.stringify({
      editAreas: editAreas,
      spreadAreas: spreadAreas,
    });
    const compressedData = compressJson(jsonData, "v2"); // encodeduri because of Safari
    const mutationCreateArConfiguration = gql(`
        mutation createArConfiguration($compressedData: String $summary: String) {
          createArConfiguration(documentId: "${storeDocumentId}", fileId: "${fileId}", userId: "${userId}", type: "${type}", title: "${title}", topImageUrl: "${topImageUrl}", data: $compressedData, summary: $summary ) {
            id
            modified
          }
      }`);

    try {
      // create a new entry if there was no result
      let result = this.client.mutate({
        mutation: mutationCreateArConfiguration,
        variables: { compressedData: compressedData, summary: summary },
      });
      return result;
    } catch (err) {
      console.log("ERROR createDocumentConfiguration", err);
      return null;
    }
  }

  async updateDocumentConfiguration(id, jsonData) {
    if (!id) {
      console.log("ERROR updateDocumentConfiguration no id");
      return;
    }
    // compression and dataSize
    let compressedData = compressJson(jsonData, "v2");
    const compressedSize = getObjectSize(compressedData);
    console.log("INFO configuration size", compressedSize);
    const dataSize = compressedSize.toString();

    const title = jsonData.caption;
    const topImageUrl = jsonData.topImageUrl;
    const collaboratorEmails = jsonData.collaboratorEmails ?? "";
    // summary
    const editAreas = jsonData.editAreas ?? [];
    const spreadAreas = jsonData.spreadAreas ?? [];
    let summary = JSON.stringify({
      editAreas: editAreas,
      spreadAreas: spreadAreas,
    });
    const updateArConfiguration = gql(`
      mutation updateArConfiguration($compressedData: String $summary: String) {
        updateArConfiguration(id: "${id}", title: "${title}", topImageUrl: "${topImageUrl}", dataSize: "${dataSize}", data: $compressedData, summary: $summary, collaboratorEmails: "${collaboratorEmails}") {
          id
          modified
        }
      }`);

    try {
      // do we need to upload the configuration to S3?
      // get a time limited upload url and upload it
      if (compressedSize > 200000) {
        console.log(
          "INFO configurationSize may be too big for database storage"
        );
        const tenantId = this.rootStore.main.userEmail;
        const uploadUrl = await this.getConfigurationDataUploadUrl(
          id,
          tenantId
        );
        if (uploadUrl) {
          await axios.put(uploadUrl, compressedData);
          // remove the configuration if it is too big for DynamoDB
          if (compressedSize > 390000) compressedData = "";
        } else {
          console.log("ERROR get no configuration Upload Url");
        }
      }
    } catch (err) {
      console.log("ERROR updateDocumentConfiguration upload to S3", err);
    }

    try {
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: updateArConfiguration,
        variables: { compressedData: compressedData, summary: summary },
      });
      console.log("INFO updateDocumentConfiguration successfull", id);

      return result;
    } catch (err) {
      console.log("ERROR updateDocumentConfiguration", err);
      return false;
    }
  }

  async deleteDocumentConfiguration(id) {
    const config = await this.getConfiguration(id);

    // ===========================================
    // PRIOR TO DELETION
    // ===========================================
    if (config?.type === "service") {
      // if it is a service configuration, we need to delete the service first
      const serviceStatus = await this.rootStore.services.getServiceStatus(id);
      console.log("INFO service status before deletion", serviceStatus);
      if (serviceStatus === "active") {
        await this.rootStore.services.removeService(id);
      }
    }
    // ===========================================
    // DELETE THE CONFIGURATION
    // ===========================================
    const mutationDeleteArConfiguration = gql(`
      mutation deleteArConfiguration {
        deleteArConfiguration(id: "${id}") {
        id
        title
        }
      }`);

    try {
      // create a new entry if there was no result
      let result = this.client.mutate({
        mutation: mutationDeleteArConfiguration,
      });
      return result;
    } catch (err) {
      console.log("ERROR deleteDocumentConfiguration", err);
      return null;
    }
  }

  async getConfiguration(configurationId) {
    if (!configurationId) return [];

    const queryGetConfiguration = gql(`
      query getArConfiguration {
        getArConfiguration(id: "${configurationId}") {
          id
          documentId
          userId
          type
          title
          topImageUrl
          collaboratorEmails
          data
          dataSize
          created
          modified
          version
          isDeleted
          isArchived
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetConfiguration,
        fetchPolicy: "network-only",
      });
      let configuration = awsResult?.data?.getArConfiguration;
      // decompress the data
      if (configuration) {
        const dataSize = configuration?.dataSize
          ? parseInt(configuration.dataSize)
          : 0;
        let decompressedData = configuration.data
          ? decompressJson(configuration.data)
          : null;

        // ==========================================
        // the data is empty - try to get it from S3
        // ==========================================
        if (!decompressedData && (dataSize > 20000 || dataSize === 0)) {
          const tenantId = this.rootStore.main.userEmail;
          const downloadUrl = await this.getConfigurationDataDownloadUrl(
            configurationId,
            tenantId
          );
          if (downloadUrl) {
            // const result = await axios.get(downloadUrl);
            const result = await axios(downloadUrl, {
              // query URL without using browser cache
              headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
              },
            });
            decompressedData = result?.data
              ? decompressJson(result.data)
              : null;
          } else {
            console.log("ERROR get no configuration Download Url");
          }
        }
        configuration.dataJson = decompressedData ?? {};
      }
      return configuration;
    } catch (err) {
      console.log("ERROR getConfiguration", err);
      return [];
    }
  }

  async getConfigurationAsTemplate(configurationId) {
    if (!configurationId) return [];

    const queryGetConfiguration = gql(`
      query getArConfiguration {
        getArConfiguration(id: "${configurationId}") {
          id
          userId
          type
          title
          topImageUrl
          dataSize
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetConfiguration,
        fetchPolicy: "network-only",
      });
      let configuration = awsResult?.data?.getArConfiguration;
      // decompress the data
      if (configuration) {
      }
      return configuration;
    } catch (err) {
      console.log("ERROR getConfigurationAsTemplate", err);
      return [];
    }
  }

  async duplicateConfiguration(item) {
    if (!item || !item.id) return;

    let configurationId = item.id;
    let oldConfiguration = await this.getConfiguration(configurationId);
    try {
      let documentId = oldConfiguration.documentId;
      let fileId = item.fileId;
      let type = oldConfiguration.type;
      let title = oldConfiguration.title + " copy";
      let topImageUrl = getDummyImage();
      let jsonData = oldConfiguration.dataJson;
      await this.createDocumentConfiguration(
        documentId,
        fileId,
        type,
        title,
        topImageUrl,
        jsonData
      );
    } catch (err) {
      console.log("ERROR getConfiguration", err);
      return [];
    }
  }

  async getConfigurationDataUploadUrl(configurationId, tenantId) {
    if (!configurationId || !tenantId) return true;

    const getConfigurationDataUploadUrl = gql(`
      query getConfigurationDataUploadUrl {
        getArConfigurationUploadUrl(configurationId: "${configurationId}", tenantId: "${tenantId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getConfigurationDataUploadUrl,
        fetchPolicy: "network-only",
      });
      const awsUploadUrl = awsResult?.data?.getArConfigurationUploadUrl;

      return awsUploadUrl;
    } catch (err) {
      console.log("ERROR getConfigurationDataUploadUrl", err);
    }
  }

  async getConfigurationDataDownloadUrl(configurationId, tenantId) {
    if (!configurationId || !tenantId) return true;

    const getConfigurationDataDownloadUrl = gql(`
      query getConfigurationDataDownloadUrl {
        getArConfigurationDownloadUrl(configurationId: "${configurationId}", tenantId: "${tenantId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getConfigurationDataDownloadUrl,
        fetchPolicy: "network-only",
      });
      const awsDownloadUrl = awsResult?.data?.getArConfigurationDownloadUrl;
      return awsDownloadUrl;
    } catch (err) {
      console.log("ERROR getConfigurationDataDownloadUrl", err);
    }
  }

  async getDocumentConfigurations(documentId, type) {
    if (!documentId) return [];

    const queryGetDocumentConfigurations = gql(`
      query queryArConfigurationsByDocumentId {
        queryArConfigurationsByDocumentId(documentId: "${documentId}") {
          items {
            id
            documentId
            userId
            type
            title
            topImageUrl
            collaboratorEmails
            summary
            created
            modified
          }
          nextToken
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetDocumentConfigurations,
        fetchPolicy: "network-only",
      });
      const documentConfigurations =
        awsResult?.data?.queryArConfigurationsByDocumentId?.items;
      // awsResult?.data?.queryArConfigurationsByDocumentId?.items.filter(
      //   (item) => !item.isArchived && !item.isDeleted,
      // );
      if (!documentConfigurations) return [];
      // create a list
      let configurations = [];
      documentConfigurations.forEach((configuration) => {
        if (["calculation", "service", "view"].includes(configuration.type)) {
          let summary = getAsJson(configuration.summary);
          const hasSpreadOrEditAreas =
            summary?.editAreas?.length > 0 || summary?.spreadAreas?.length > 0;
          // add
          configurations.push({
            id: configuration.id,
            userId: configuration.userId,
            title: configuration.title,
            type: configuration.type,
            elementType: hasSpreadOrEditAreas ? "share" : "",
            created: configuration.created,
            modified: configuration.modified,
            topImageUrl: configuration.topImageUrl,
            collaboratorEmails: configuration.collaboratorEmails,
            summary: summary,
          });
        }
      });
      return configurations;
    } catch (err) {
      console.log("ERROR getDocumentConfigurations", err);
      return [];
    }
  }

  async getDocumentSuggestions(documentInfo, templateInfo, type, callback) {
    if (!templateInfo) return [];
    let linkedConfigurations = templateInfo.linkedConfigurations;
    if (!linkedConfigurations || linkedConfigurations.length === 0) {
      callback && callback([]);
      return [];
    }

    let list = [];
    try {
      for (const configurationId of linkedConfigurations) {
        let configuration =
          await this.getConfigurationAsTemplate(configurationId);
        if (configuration) {
          configuration.type = "suggestion";
          list.push(configuration);
        }
      }
    } catch (err) {
      console.log("ERROR getDocumentSuggestions", err);
    }
    if (callback) {
      callback(list);
    } else return list;
  }

  async getUserConfigurations(userId, type, refresh = false) {
    if (!userId) return [];

    const queryGetUserConfigurations = gql(`
      query queryArConfigurationsByUserId {
        queryArConfigurationsByUserId(userId: "${userId}") {
          items {
            id
            documentId
            userId
            collaboratorEmails
            type
            title
            topImageUrl
            created
            modified
            version
          }
          nextToken
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetUserConfigurations,
        fetchPolicy: refresh && "network-only",
      });
      const userConfigurations =
        awsResult?.data?.queryArConfigurationsByUserId?.items;
      // awsResult?.data?.queryArConfigurationsByUserId?.items.filter(
      //   (item) => !item.isArchived && !item.isDeleted,
      // );
      if (!userConfigurations) return [];
      // create a list
      let configurations = [];
      let services = [];
      userConfigurations.forEach((configuration) => {
        if (configuration.type === "calculation") {
          configurations.push({
            id: configuration.id,
            userId: configuration.userId,
            fileId: configuration.documentId,
            fileType: "localfile",
            title: configuration.title ?? "New Calculation",
            created: configuration.created,
            modified: configuration.modified,
            topImageUrl: configuration.topImageUrl,
            collaboratorEmails: configuration.collaboratorEmails,
          });
        } else if (configuration.type === "service") {
          services.push({
            id: configuration.id,
            userId: configuration.userId,
            fileId: configuration.documentId,
            fileType: configuration.fileType ?? "localfile",
            elementType: "service",
            title: configuration.title ?? "New Service",
            created: configuration.created,
            modified: configuration.modified,
            topImageUrl: configuration.topImageUrl,
            collaboratorEmails: configuration.collaboratorEmails,
          });
        }
      });
      return { configurations, services };
    } catch (err) {
      console.log("ERROR getUserConfigurations", err);
      return null;
    }
  }

  async getSharedFileIdConfigurations(fileId, type) {
    if (!fileId) return [];
    let userId = this.rootStore.main.userEmail;
    const queryGetFileConfigurations = gql(`

    query queryArConfigurationsByFileId {
      queryArConfigurationsByFileId(fileId: "${fileId}", userId: "${userId}") {
        items {
          id
          documentId
          userId
          type
          title
          topImageUrl
          summary
          created
          modified
          collaboratorEmails
        }
        nextToken
      }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetFileConfigurations,
        fetchPolicy: "network-only",
      });
      const fileConfigurations =
        awsResult?.data?.queryArConfigurationsByFileId?.items.filter(
          (item) => !item.isArchived && !item.isDeleted
        );
      if (!fileConfigurations) return [];
      // create a list
      let configurations = [];
      fileConfigurations.forEach((configuration) => {
        if (configuration.type === "calculation") {
          let summary = getAsJson(configuration.summary);
          const hasSpreadOrEditAreas =
            summary?.editAreas?.length > 0 || summary?.spreadAreas?.length > 0;
          // add
          configurations.push({
            id: configuration.id,
            userId: configuration.userId,
            title: configuration.title,
            type: configuration.type,
            elementType: hasSpreadOrEditAreas ? "share" : "",
            created: configuration.created,
            modified: configuration.modified,
            topImageUrl: configuration.topImageUrl,
            collaboratorEmails: configuration.collaboratorEmails,
            summary: summary,
          });
        }
      });
      return configurations;
    } catch (err) {
      console.log("ERROR getFileIdConfigurations", err);
      return [];
    }
  }

  // ===============================
  //
  // HOSTED FILES
  //
  // ===============================

  async getHostedFile(fileId) {
    if (!fileId) return [];

    const queryGetHostedFile = gql(`
      query getArHostedFile {
        getArHostedFile(id: "${fileId}") {
          id
          parentId
          domainId
          userId
          title
          description
          fileName
          fileUrl
          fileInfo
          data
          dataSize
          templateInfo
          topImageUrl
          collaboratorEmails
          created
          modified
          version
          isDeleted
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetHostedFile,
        fetchPolicy: "network-only",
      });
      let result = awsResult?.data?.getArHostedFile;
      if (result) {
        result.fileId = fileId;
        result.fileType = "hostedfile";
        if (result.templateInfo)
          result.templateInfo = getAsJson(result.templateInfo);
        if (result.fileInfo) result.fileInfo = getAsJson(result.fileInfo);

        //========================================
        // is the data too big for the database?
        //========================================
        if (!result.data) {
          const dataSize = tryParseInt(result?.dataSize);
          if (dataSize > 100000 && result.domainId && fileId) {
            const downloadUrl = await this.getHostedFileDownloadUrl(
              fileId,
              result?.domainId
            );
            if (downloadUrl) {
              const data = await axios(downloadUrl, {
                // query URL without using browser cache
                headers: {
                  "Cache-Control": "no-cache",
                  Pragma: "no-cache",
                  Expires: "0",
                },
              });
              result.data = data?.data;
            } else {
              console.log("ERROR get no hostedFile Download Url");
            }
          }
        }
      }
      return result;
    } catch (err) {
      console.log("ERROR getHostedFile", err);
      return [];
    }
  }

  async getHostedFilesForUser() {
    const userId = this.rootStore.main.userEmail;
    const queryGetHostedFiles = gql(`
      query queryArHostedFilesByUserId {
        queryArHostedFilesByUserId(userId: "${userId}") {
        items 
          {
            id
            parentId
            domainId
            userId
            description
            fileName
            fileUrl
            dataSize
            topImageUrl
            collaboratorEmails
            linkedTables
            created
            modified
            version
            isDeleted
            isArchived
          }
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetHostedFiles,
        fetchPolicy: "network-only",
      });
      const hostedFiles = awsResult?.data?.queryArHostedFilesByUserId?.items
        .filter((item) => !item.isArchived && !item.isDeleted)
        .sort((a, b) => {
          return new Date(b.modified) - new Date(a.modified);
        });

      if (!hostedFiles) return [];
      hostedFiles.forEach((file) => {
        if (file.dataSize) {
          file.sizeKb = numeral(file.dataSize).format("0 b");
        }
        if (
          file.linkedTables &&
          is.string(file.linkedTables) &&
          file.linkedTables.length > 0
        ) {
          file.linkedTables = file.linkedTables?.split(",");
        }
      });

      return hostedFiles;
    } catch (err) {
      console.log("ERROR getHostedFiles", err);
      return [];
    }
  }

  async createHostedFile(fileId = "", jsonData = null, templateInfo) {
    if (!fileId) {
      console.log("ERROR createHostedFile no fileId");
      return;
    }

    const userId = this.rootStore.main.getUserEmailOrId();
    const domainId = this.rootStore.main.userDomain;
    const fileName =
      templateInfo?.fileTitle ?? "New " + formatDateTimeForFileName();
    const fileUrl = "";
    const topImageUrl = getDummyImage();
    // const templateInfoString = templateInfo ? JSON.stringify(templateInfo) : "";

    try {
      const mutationCreateArHostedFile = gql(`
        mutation createArHostedFile {
          createArHostedFile(id: "${fileId}", userId: "${userId}", domainId: "${domainId}", fileName: "${fileName}", fileUrl: "${fileUrl}", topImageUrl: "${topImageUrl}" ) {
            id
            modified
          }
      }`);
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: mutationCreateArHostedFile,
        // variables: { compressedData: compressedData, summary: summary },
      });
      const id = result?.data?.createArHostedFile?.id;
      return id;
    } catch (err) {
      console.log("ERROR createHostedFile", err);
      return null;
    }
  }

  async updateHostedFile(fileData, spreadJson) {
    let id = fileData?.fileId;
    if (!id) {
      console.log("ERROR updateHostedFile no id");
      return;
    }
    // compression and dataSize
    let compressedData = compressJson(spreadJson, "v2");
    const compressedSize = getObjectSize(compressedData);
    console.log("INFO hostedFile size", compressedSize);
    const dataSize = compressedSize.toString();

    // =========================
    // get the linked tables
    // =========================
    let linkedTables = [];
    const linkedTablesCount = spreadJson?.dataManager?.tables?.length ?? 0;
    if (linkedTablesCount > 0) {
      const tables = spreadJson?.dataManager?.tables ?? [];
      tables.forEach((table) => {
        if (table?.name) linkedTables.push(table.name);
      });
    }
    const linkedTablesString = linkedTables.toString();
    const fileName = fileData.fileName ?? "N/A";
    const topImageUrl = fileData.topImageUrl ?? "";
    const templateInfo = fileData.templateInfo
      ? JSON.stringify(fileData.templateInfo)
      : "";
    const fileInfo = "";
    const collaboratorEmails = fileData.collaboratorEmails ?? "";
    const updateArHostedFile = gql(`
      mutation updateArHostedFile($compressedData: String,$templateInfo: String,$fileInfo: String) {
        updateArHostedFile(id: "${id}", fileName: "${fileName}", topImageUrl: "${topImageUrl}", dataSize: "${dataSize}", linkedTables: "${linkedTablesString}", data: $compressedData, templateInfo: $templateInfo, fileInfo: $fileInfo, collaboratorEmails: "${collaboratorEmails}" ) {
          id
          modified
        }
      }`);

    try {
      // do we need to upload the hostedFile to S3?
      // get a time limited upload url and upload it
      if (compressedSize > 100000) {
        console.log("INFO HostedFile may be too big for database storage");
        const domainId = this.rootStore.main.userDomain;
        const uploadUrl = await this.getHostedFileUploadUrl(id, domainId);
        if (uploadUrl) {
          await axios.put(uploadUrl, compressedData);
          // remove the configuration if it is too big for DynamoDB
          if (compressedSize > 100000) compressedData = "";
        } else {
          console.log("ERROR get no hostedFile Upload Url");
        }
      }
    } catch (err) {
      console.log("ERROR updateHostedFile upload to S3", err);
    }

    try {
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: updateArHostedFile,
        variables: {
          compressedData: compressedData,
          templateInfo: templateInfo,
          fileInfo: fileInfo,
        },
      });
      console.log("INFO updateHostedFile successfull", id);

      return result;
    } catch (err) {
      console.log("ERROR updateHostedFile", err);
      return false;
    }
  }

  async getHostedFileUploadUrl(fileId, domainId) {
    if (!fileId || !domainId) return true;

    const getHostedFileUploadUrl = gql(`
      query getHostedFileUploadUrl {
        getArHostedFileUploadUrl(domainId: "${domainId}", hostedFileId: "${fileId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getHostedFileUploadUrl,
        fetchPolicy: "network-only",
      });
      const awsUploadUrl = awsResult?.data?.getArHostedFileUploadUrl;

      return awsUploadUrl;
    } catch (err) {
      console.log("ERROR getHostedFileUploadUrl", err);
    }
  }

  async getHostedFileDownloadUrl(fileId, domainId) {
    if (!fileId || !domainId) return true;

    const getHostedFileDownloadUrl = gql(`
      query getHostedFileDownloadUrl {
        getArHostedFileDownloadUrl(domainId: "${domainId}", hostedFileId: "${fileId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getHostedFileDownloadUrl,
        fetchPolicy: "network-only",
      });
      const awsDownloadUrl = awsResult?.data?.getArHostedFileDownloadUrl;
      return awsDownloadUrl;
    } catch (err) {
      console.log("ERROR getHostedFileDownloadUrl", err);
    }
  }

  async uploadHostedTableData(fileId, data) {
    let id = fileId ?? nanoid();

    let saveData = compressJson(data);
    let objectSize = getObjectSize(saveData);
    try {
      // get a time limited upload url and upload it
      const domainId = this.rootStore.main.userDomain;
      const uploadUrl = await this.getHostedFileUploadUrl(id, domainId);
      if (uploadUrl) {
        await axios.put(uploadUrl, saveData);
        let result = { fileId: id, domainId: domainId, size: objectSize };
        return result;
      } else {
        console.log("ERROR get no hostedFile Upload Url");
      }
    } catch (err) {
      console.log("ERROR updateHostedFile upload to S3", err);
    }
  }

  async downloadHostedTableData(item) {
    if (!item) return;
    let id = item?.id;
    let domainId = item?.domainId;
    try {
      // get a time limited upload url and upload it
      const downloadUrl = await this.getHostedFileDownloadUrl(id, domainId);
      if (downloadUrl) {
        let awsResult = await axios.get(downloadUrl);
        let data = awsResult?.data;
        let decompressedData = data ? decompressJson(data) : null;
        return decompressedData;
      } else {
        console.log("ERROR get no hostedFile download Url");
      }
    } catch (err) {
      console.log("ERROR updateHostedFile download from S3", err);
    }
  }

  async deleteHostedFile(fileId) {
    const mutationDeleteArHostedFile = gql(`
      mutation deleteArHostedFile {
        deleteArHostedFile(id: "${fileId}") {
          id
          title
        }
      }`);

    try {
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: mutationDeleteArHostedFile,
      });
      return result;
    } catch (err) {
      console.log("ERROR deleteHostedFile", err);
      return null;
    }
  }

  // ===============================
  //
  // SUBMITS
  //
  // ===============================

  async createDocumentSubmit(
    documentId,
    configurationId,
    invitationId,
    fileId,
    title = "Submit",
    jsonData = null,
    jsonSummary = null
  ) {
    const userId = this.rootStore.main.getUserEmailOrId();
    let submitStatus = jsonData?.status;
    let submitData = jsonData?.data;
    let emailNotification = submitData?.emailNotification;
    if (!emailNotification)
      emailNotification = jsonData?.submitNotificationEmail;
    if (!fileId || fileId === "") fileId = getFileIdFromDocumentId(documentId);

    if (emailNotification === undefined || emailNotification === null)
      emailNotification = "";
    let comment = jsonData?.comment ?? "";
    let data = jsonData ? JSON.stringify(jsonData) : "";
    let summary = jsonSummary ? JSON.stringify(jsonSummary) : "";
    const mutationCreateArSubmit = gql(`
      mutation CreateArSubmit($data: AWSJSON, $summary: AWSJSON) {
        createArSubmit(documentId: "${documentId}", configurationId: "${configurationId}",  invitationId:  "${invitationId}",  fileId:  "${fileId}", title:  "${title}",  emailSender:  "${userId}", emailNotification: "${emailNotification}", status:  "${submitStatus}",  comment:  "${comment}", data: $data, summaryData: $summary) {
          id
          documentId
          fileId
          configurationId
          invitationId
          emailSender
          title
          status
          comment
          summaryData
          created
          modified
        }
      }`);

    try {
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: mutationCreateArSubmit,
        variables: { data: data, summary: summary },
      });
      console.log("INFO create Submit", result);
      // write a notification
      if (emailNotification && emailNotification !== "undefined") {
        this.createNotification({
          emailCreator: this.rootStore.main.getUserEmailOrId(),
          emailReceiver: emailNotification,
          data: {
            invitationId: invitationId,
            documentId: documentId,
            elementId: configurationId,
            status: submitStatus,
          },
          type: "create-submit",
          header: "new submit: " + title,
          content: comment ?? "",
        });
      }
      return { type: "success", message: "" };
    } catch (err) {
      console.log("ERROR createDocumentSubmit", err);
      return { type: "error", message: err?.message };
    }
  }

  async deleteDocumentSubmit(id) {
    const mutationDeleteArSubmit = gql(`
      mutation deleteArSubmit {
        updateArSubmitIsDeleted(id: "${id}") {
        id
        title
        }
      }`);

    try {
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: mutationDeleteArSubmit,
      });
      return result;
    } catch (err) {
      console.log("ERROR deleteDocumentSubmit", err);
      return null;
    }
  }

  async archiveDocumentSubmit(id) {
    const mutationArchiveArSubmit = gql(`
      mutation archiveArSubmit {
        updateArSubmitIsArchived(id: "${id}") {
        id
        title
        }
      }`);

    try {
      // create a new entry if there was no result
      let result = await this.client.mutate({
        mutation: mutationArchiveArSubmit,
      });
      return result;
    } catch (err) {
      console.log("ERROR archiveDocumentSubmit", err);
      return null;
    }
  }

  async getSubmit(submitId) {
    if (!submitId) return [];

    const queryGetSubmit = gql(`
      query getArSubmit {
        getArSubmit(id: "${submitId}") {
          id
          documentId
          invitationId
          emailSender
          type
          title
          data
          summaryData
          created
          modified
          version
          isDeleted
          isArchived
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetSubmit,
        fetchPolicy: "network-only",
      });
      let submit = awsResult?.data?.getArSubmit;
      return submit;
    } catch (err) {
      console.log("ERROR getSubmit", err);
      return [];
    }
  }

  async getDocumentSubmits(documentId, type) {
    if (!documentId) return [];
    const queryGetDocumentSubmits = gql(`
      query queryArSubmitsByDocumentId {
        queryArSubmitsByDocumentId(documentId: "${documentId}") {
          items {
            id
            documentId
            configurationId
            invitationId
            emailSender
            type
            title
            data
            summaryData
            created
            modified
          }
          nextToken
        }
    }`);

    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetDocumentSubmits,
        fetchPolicy: "network-only",
      });
      const documentSubmits =
        awsResult?.data?.queryArSubmitsByDocumentId?.items;
      // awsResult?.data?.queryArSubmitsByDocumentId?.items.filter(
      //   (item) => !item.isArchived && !item.isDeleted,
      // );
      if (!documentSubmits) return [];
      // create a list
      let submits = [];
      documentSubmits.forEach((submit) => {
        let summary = getAsJson(submit.summaryData);
        let range = summary.ranges?.length > 0 ? summary.ranges[0] : "";
        submits.push({
          id: submit.id,
          documentId: submit.documentId,
          invitationId: submit.invitationId,
          configurationId: submit.configurationId,
          emailSender: submit.emailSender,
          title: submit.title,
          created: submit.created,
          modified: submit.modified,
          data: getAsJson(submit.data),
          range: range,
          summaryData: summary,
        });
      });
      return submits;
    } catch (err) {
      console.log("ERROR getDocumentSubmits", err);
      return [];
    }
  }

  // ===============================
  //
  // CACHE
  //
  // ===============================

  async getCacheDataUploadUrl(workbookId, fileId, versionId) {
    let tenantId = this.rootStore.main.userTenantId;
    if (!workbookId || !tenantId) return;

    const getArCacheFileUploadUrl = gql(`
      query getArCacheFileUploadUrl {
        getArCacheFileUploadUrl(workbookId: "${workbookId}", tenantId: "${tenantId}", fileId: "${fileId}", versionId: "${versionId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getArCacheFileUploadUrl,
        fetchPolicy: "network-only",
      });
      const awsUploadUrl = awsResult?.data?.getArCacheFileUploadUrl;
      return awsUploadUrl;
    } catch (err) {
      console.log("ERROR getArCacheFileUploadUrl", err);
    }
  }

  async getPublicCacheDataUploadUrl(
    publicUserId,
    workbookId,
    fileId,
    versionId
  ) {
    if (!workbookId || !publicUserId) return;

    const getArCacheFileUploadUrl = gql(`
      query getArCacheFileUploadUrl {
        getArCacheFileUploadUrl(workbookId: "${workbookId}", tenantId: "${publicUserId}", fileId: "${fileId}", versionId: "${versionId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getArCacheFileUploadUrl,
        fetchPolicy: "network-only",
      });
      const awsUploadUrl = awsResult?.data?.getArCacheFileUploadUrl;
      return awsUploadUrl;
    } catch (err) {
      console.log("ERROR getArCacheFileUploadUrl public", err);
    }
  }

  async getCacheDataDownloadUrl(tenantId, workbookId, fileId, versionId) {
    if (!workbookId || !tenantId) return;

    const getArCacheFileDownloadUrl = gql(`
      query getArCacheFileDownloadUrl {
        getArCacheFileDownloadUrl(workbookId: "${workbookId}", tenantId: "${tenantId}", fileId: "${fileId}", versionId: "${versionId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: getArCacheFileDownloadUrl,
        fetchPolicy: "network-only",
      });
      const awsDownloadUrl = awsResult?.data?.getArCacheFileDownloadUrl;
      return awsDownloadUrl;
    } catch (err) {
      console.log("ERROR getArCacheFileDownloadUrl", err);
    }

    // lets get a direct download URL
    console.log("INFO: get direct URL");
    const bucketUrl = "https://airrange-cache.s3.eu-central-1.amazonaws.com/";
    let directUrl =
      bucketUrl + tenantId + "/" + workbookId + "/" + fileId + versionId;
    return directUrl;
  }

  async deleteCacheData(workbookId, fileId, versionId, tenantId) {
    let tenant = tenantId ?? this.rootStore.main.userTenantId;
    if (!workbookId || !tenant) return;

    const DeleteArCacheFile = gql(`
      query deleteArCacheFile {
        deleteArCacheFile(tenantId: "${tenant}", workbookId: "${workbookId}", fileId: "${fileId}", versionId: "${versionId}")
      }`);

    try {
      // create a new entry if there was no result
      let awsResult = await this.client.query({
        query: DeleteArCacheFile,
      });
      const awsDownloadUrl = awsResult?.data?.deleteArCacheFile;
      return awsDownloadUrl;
    } catch (err) {
      console.log("ERROR deleteArCacheFile", err);
    }
  }

  // ===============================
  //
  // SUBSCRIPTIONS
  //
  // ===============================

  async getSubscriptions(invitationId, params = {}) {
    const queryGetSubscriptions = gql(`
      query getSubscription {
        listInvitationSubscriptions(
          filter: {
            invitationId: { eq: "${invitationId}" },
            ${params.userId ? `userId: { eq: "${params.userId}" },` : ""}
            ${
              params.userEmail
                ? `userEmail: { eq: "${params.userEmail}" },`
                : ""
            }
            ${
              params.hasOwnProperty("isTrial")
                ? `isTrial: { eq: ${params.isTrial} }`
                : ""
            }
          }
        ) {
          items {
            id
            createdAt
            endDate
            invitationId
            isTrial
            paymentAmount
            startDate
            stripeCustomerId
            stripeEventId
            stripePaymentId
            userEmail
            userId
          }
        }
      }
    `);
    try {
      // create a new entry if there was no result
      const awsResult = await this.client.query({
        query: queryGetSubscriptions,
        fetchPolicy: "network-only",
      });
      return awsResult?.data?.listInvitationSubscriptions?.items || [];
    } catch (err) {
      console.log("ERROR getSubscription", err);
    }
    return false;
  }

  async createSubscription(data) {
    const mutationCreateSubscription = gql(`
      mutation createInvitationSubscription {
        createInvitationSubscription(input: {
          invitationId: "${data.invitationId}",
          userId: "${data.userId}",
          userEmail: "${data.userEmail}",
          isTrial: ${data.isTrial},
          startDate: "${format(data.startDate, "yyyy-MM-dd")}",
          endDate: "${format(data.endDate, "yyyy-MM-dd")}",
          paymentAmount: 0,
          createdAt: "${new Date().toISOString()}"
        }) {
          id
          createdAt
          endDate
          invitationId
          isTrial
          paymentAmount
          startDate
          stripeCustomerId
          stripeEventId
          stripePaymentId
          userEmail
          userId
        }
    }`);

    try {
      // create a new entry if there was no result
      const newSubscription = await this.client.mutate({
        mutation: mutationCreateSubscription,
      });
      return newSubscription?.data?.createInvitationSubscription;
    } catch (err) {
      console.log("ERROR create subscription", err);
    }
  }
}
